import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaHistory,
  FaHome,
  FaCalendarCheck,
  FaRupeeSign,
  FaBell,
  FaArrowRight,
  FaBuilding,
  FaChartPie,
} from 'react-icons/fa';
import { useWorkspace } from '../../context/WorkspaceContext';
import WorkspaceStaffPanel from './WorkspaceStaffPanel';
import './WorkspaceDashboard.css';

const MODULES = [
  {
    id: 'staff',
    to: '/workspace/staff',
    icon: FaUsers,
    title: 'Staff management',
    desc: 'Invite links, authorized emails & team roles',
    permission: 'manage_staff',
    statKey: 'staffCount',
    accent: 'violet',
  },
  {
    id: 'activity',
    to: '/workspace/activity',
    icon: FaHistory,
    title: 'Activity & logs',
    desc: 'Monitor all workspace actions',
    permission: 'view_activity',
    accent: 'slate',
  },
  {
    id: 'properties',
    to: '/workspace/properties',
    icon: FaHome,
    title: 'Property listings',
    desc: 'View and manage company stay listings',
    permission: 'view_properties',
    statKey: 'propertyCount',
    accent: 'blue',
  },
  {
    id: 'bookings',
    to: '/workspace/bookings',
    icon: FaCalendarCheck,
    title: 'Booking management',
    desc: 'Review and update booking status',
    permission: 'manage_bookings',
    statKey: 'totalBookings',
    accent: 'amber',
  },
  {
    id: 'revenue',
    to: '/workspace/analytics',
    icon: FaRupeeSign,
    title: 'Revenue & analytics',
    desc: 'Earnings, commissions & payment reports',
    permission: 'view_analytics',
    statKey: 'revenue',
    accent: 'emerald',
  },
  {
    id: 'notifications',
    to: '/workspace/notifications',
    icon: FaBell,
    title: 'Notifications & reports',
    desc: 'Company alerts and booking updates',
    permission: 'view_notifications',
    accent: 'rose',
  },
];

const STATUS_COLORS = {
  pending: 'status-pending',
  confirmed: 'status-confirmed',
  approved: 'status-confirmed',
  cancelled: 'status-cancelled',
  declined: 'status-cancelled',
  completed: 'status-completed',
  checked: 'status-completed',
  expired: 'status-expired',
};

const WorkspaceDashboard = () => {
  const { loading, error, stats, company, permissions } = useWorkspace();

  const visibleModules = useMemo(
    () => MODULES.filter((m) => permissions[m.permission]),
    [permissions]
  );

  const companyLabel = company?.name || company?.profile?.displayName || 'Your company';

  const getStat = (mod) => {
    if (!stats) return null;
    if (mod.statKey === 'staffCount') return `${stats.staffCount} members`;
    if (mod.statKey === 'propertyCount') return `${stats.propertyCount} listings`;
    if (mod.statKey === 'totalBookings') return `${stats.totalBookings} bookings`;
    if (mod.statKey === 'revenue') {
      return `₹${Number(stats.revenue?.netBalance || 0).toLocaleString('en-IN')}`;
    }
    return null;
  };

  if (error) return <p className="workspace-alert error">{error}</p>;
  if (loading || !stats) {
    return (
      <div className="workspace-dashboard workspace-dashboard-loading">
        <div className="ws-loading-hero" />
        <div className="ws-loading-kpis">
          <div /><div /><div /><div />
        </div>
        <p className="workspace-loading">Loading workspace...</p>
      </div>
    );
  }

  return (
    <div className="workspace-dashboard">
      <section className="ws-hero">
        <div className="ws-hero-content">
          <span className="ws-hero-badge">
            <FaBuilding /> Workspace
          </span>
          <h1 className="ws-hero-title">Company performance dashboard</h1>
          <p className="ws-hero-subtitle">
            Welcome back — manage {companyLabel}&apos;s team, listings, bookings and revenue from one place.
          </p>
        </div>
        <div className="ws-hero-accent ws-hero-accent-1" aria-hidden />
        <div className="ws-hero-accent ws-hero-accent-2" aria-hidden />
      </section>

      <div className="ws-kpi-grid">
        <article className="ws-kpi-card ws-kpi-blue">
          <div className="ws-kpi-icon"><FaHome /></div>
          <div>
            <p>Properties</p>
            <strong>{stats.propertyCount}</strong>
          </div>
        </article>
        <article className="ws-kpi-card ws-kpi-violet">
          <div className="ws-kpi-icon"><FaUsers /></div>
          <div>
            <p>Team members</p>
            <strong>{stats.staffCount}</strong>
          </div>
        </article>
        <article className="ws-kpi-card ws-kpi-amber">
          <div className="ws-kpi-icon"><FaCalendarCheck /></div>
          <div>
            <p>Total bookings</p>
            <strong>{stats.totalBookings}</strong>
          </div>
        </article>
        <article className="ws-kpi-card ws-kpi-emerald">
          <div className="ws-kpi-icon"><FaRupeeSign /></div>
          <div>
            <p>Net revenue</p>
            <strong>₹{Number(stats.revenue?.netBalance || 0).toLocaleString('en-IN')}</strong>
          </div>
        </article>
      </div>

      {permissions?.manage_staff && (
        <div className="ws-staff-shell">
          <WorkspaceStaffPanel companyInviteCode={company?.inviteCode} />
        </div>
      )}

      <section className="ws-section">
        <div className="ws-section-head">
          <h2><FaChartPie /> All workspace features</h2>
          <span>{visibleModules.length} modules available</span>
        </div>
        <div className="ws-hub-grid">
          {visibleModules.map((mod) => (
            <Link key={mod.id} to={mod.to} className={`ws-hub-card ws-hub-${mod.accent}`}>
              <div className="ws-hub-icon">
                <mod.icon />
              </div>
              <div className="ws-hub-body">
                <h3>{mod.title}</h3>
                <p>{mod.desc}</p>
                {getStat(mod) && <span className="ws-hub-stat">{getStat(mod)}</span>}
              </div>
              <FaArrowRight className="ws-hub-arrow" />
            </Link>
          ))}
        </div>
      </section>

      <section className="ws-panel ws-booking-panel">
        <div className="ws-section-head">
          <h3>Booking status breakdown</h3>
          <Link to="/workspace/bookings" className="ws-inline-link">View bookings →</Link>
        </div>
        <div className="ws-status-grid">
          {Object.keys(stats.bookingsByStatus || {}).length === 0 ? (
            <div className="ws-status-card status-empty">
              <span className="ws-status-count">0</span>
              <span className="ws-status-label">No bookings yet</span>
            </div>
          ) : (
            Object.entries(stats.bookingsByStatus || {}).map(([status, count]) => (
              <div
                key={status}
                className={`ws-status-card ${STATUS_COLORS[status.toLowerCase()] || 'status-default'}`}
              >
                <span className="ws-status-count">{count}</span>
                <span className="ws-status-label">{status}</span>
              </div>
            ))
          )}
        </div>
      </section>

      {permissions?.view_activity && (stats.recentActivity || []).length > 0 && (
        <section className="ws-panel ws-activity-panel">
          <div className="ws-section-head">
            <h3>Recent activity</h3>
            <Link to="/workspace/activity" className="ws-inline-link">View all logs →</Link>
          </div>
          <ul className="ws-activity-timeline">
            {(stats.recentActivity || []).map((row) => (
              <li key={row._id}>
                <span className="ws-activity-dot" />
                <div className="ws-activity-content">
                  <span className="ws-activity-action">{row.action}</span>
                  <p>{row.summary}</p>
                  <small>{new Date(row.createdAt).toLocaleString()}</small>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default WorkspaceDashboard;
